import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, ExternalLinkIcon, Popover } from '@revont/shared-ui';

const RESOURCE_LINKS = [
  { name: 'Mission', description: 'Why Revont exists', href: '/mission', external: false },
  {
    name: 'User Guide',
    description: 'Learn how Revont works',
    href: 'https://docs.revont.ai',
    external: true,
  },
  {
    name: 'Releases',
    description: 'What is new in the product',
    href: 'https://docs.revont.ai/product-updates',
    external: true,
  },
] as const;

function Brand(): React.JSX.Element {
  return (
    <Link to="/" className="flex items-center gap-2" aria-label="Revont home">
      <img src="/revont-logo.svg" alt="" className="h-7 w-7" />
      <span className="font-heading text-xl tracking-[-0.04em] text-white">Revont</span>
    </Link>
  );
}

function ResourceMenu(): React.JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimerRef = useRef<number | null>(null);

  const cancelClose = (): void => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openOnHover = (): void => {
    cancelClose();
    setIsOpen(true);
  };

  const scheduleClose = (close?: () => void): void => {
    cancelClose();
    closeTimerRef.current = window.setTimeout(() => {
      close?.();
      setIsOpen(false);
      closeTimerRef.current = null;
    }, 150);
  };

  useEffect(() => {
    return () => {
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  return (
    <div
      className="relative"
      onFocusCapture={openOnHover}
      onMouseEnter={openOnHover}
      onMouseLeave={() => scheduleClose()}
    >
      <Popover
        open={isOpen}
        onOpenChange={nextOpen => {
          if (nextOpen) {
            openOnHover();
          } else {
            cancelClose();
            setIsOpen(false);
          }
        }}
        position="bottom-start"
        panelClassName="menu-surface w-80 p-2"
        renderTrigger={({ open }) => (
          <Button variant="ghost" size="medium" rounded="lg" ariaExpanded={open}>
            Resources
            <motion.svg
              aria-hidden="true"
              className="h-3.5 w-3.5"
              animate={{ rotate: open ? 180 : 0, y: open ? 1 : 0 }}
              fill="none"
              initial={false}
              transition={{ type: 'spring', stiffness: 420, damping: 26, mass: 0.6 }}
              viewBox="0 0 16 16"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="m4 6 4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
            </motion.svg>
          </Button>
        )}
      >
        {({ close }) => (
          <div
            className="marketing-resource-links"
            onMouseEnter={cancelClose}
            onMouseLeave={() => scheduleClose(close)}
          >
            {RESOURCE_LINKS.map(item =>
              item.external ? (
                <a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noreferrer"
                  className="marketing-resource-link"
                  onClick={close}
                >
                  <span className="min-w-0 flex-1">
                    <strong>{item.name}</strong>
                    <small>{item.description}</small>
                  </span>
                  <ExternalLinkIcon className="marketing-resource-link-icon" />
                </a>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className="marketing-resource-link"
                  onClick={close}
                >
                  <span>
                    <strong>{item.name}</strong>
                    <small>{item.description}</small>
                  </span>
                </Link>
              )
            )}
          </div>
        )}
      </Popover>
    </div>
  );
}

export function Navigation(): React.JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const closeMobile = (): void => setIsMobileOpen(false);

  return (
    <header className="marketing-nav fixed inset-x-0 top-0 z-50">
      <nav className="marketing-container flex h-[72px] items-center justify-between">
        <Brand />

        <div className="hidden items-center gap-2 md:flex">
          <Link
            to="/product"
            className="marketing-nav-link px-3 py-2 text-sm"
            aria-current={location.pathname === '/product' ? 'page' : undefined}
          >
            Product
          </Link>
          <ResourceMenu />
          <Link
            to="/pricing"
            className="marketing-nav-link px-3 py-2 text-sm"
            aria-current={location.pathname === '/pricing' ? 'page' : undefined}
          >
            Pricing
          </Link>
        </div>

        <div className="hidden md:block">
          <Button variant="primary" size="medium" rounded="lg" onClick={() => navigate('/contact')}>
            Talk to Us
          </Button>
        </div>

        <Button
          variant="ghost"
          size="medium"
          rounded="lg"
          ariaLabel={isMobileOpen ? 'Close menu' : 'Open menu'}
          ariaExpanded={isMobileOpen}
          className="h-10 w-10 md:hidden"
          onClick={() => setIsMobileOpen(current => !current)}
        >
          {isMobileOpen ? '×' : '≡'}
        </Button>
      </nav>

      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="marketing-mobile-menu border-t border-white/10 md:hidden"
          >
            <div className="marketing-container flex flex-col gap-2 py-5">
              <Link
                to="/product"
                className="marketing-nav-link px-2 py-3 text-base"
                onClick={closeMobile}
              >
                Product
              </Link>
              <Link
                to="/mission"
                className="marketing-nav-link px-2 py-3 text-base"
                onClick={closeMobile}
              >
                Mission
              </Link>
              <a
                href="https://docs.revont.ai"
                target="_blank"
                rel="noreferrer"
                className="marketing-nav-link flex items-center justify-between px-2 py-3 text-base"
                onClick={closeMobile}
              >
                <span>User Guide</span>
                <ExternalLinkIcon className="marketing-resource-link-icon" />
              </a>
              <a
                href="https://docs.revont.ai/product-updates"
                target="_blank"
                rel="noreferrer"
                className="marketing-nav-link flex items-center justify-between px-2 py-3 text-base"
                onClick={closeMobile}
              >
                <span>Releases</span>
                <ExternalLinkIcon className="marketing-resource-link-icon" />
              </a>
              <Link
                to="/pricing"
                className="marketing-nav-link px-2 py-3 text-base"
                onClick={closeMobile}
              >
                Pricing
              </Link>
              <Button
                variant="primary"
                size="medium"
                rounded="lg"
                className="mt-3 w-full"
                onClick={() => {
                  closeMobile();
                  navigate('/contact');
                }}
              >
                Talk to Us
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
